/**
 * useIsPlatformMaster
 *
 * Whether the signed-in user is the platform master. Drives menu visibility
 * ONLY — enforcement is is_platform_master() inside each RPC. A user who
 * discovers /admin/users gets an empty screen and 42501s, not data.
 *
 * @module hooks/useIsPlatformMaster
 */

import { useEffect, useState } from "react";
import { checkIsPlatformMaster } from "../services/adminService";

/**
 * @param enabled - pass false when there is no session. AppContent calls this
 *   hook unconditionally (hooks must be), including while rendering the
 *   signed-out /auth/* pages. Without this guard the RPC would run as `anon`,
 *   which 20240101000019 revokes, and every visit to the sign-in page would
 *   log an error for a question that has an obvious answer: nobody is signed
 *   in, so nobody is the master.
 */
export const useIsPlatformMaster = (
  enabled: boolean
): { isMaster: boolean; loading: boolean } => {
  const [isMaster, setIsMaster] = useState(false);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setIsMaster(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      const result = await checkIsPlatformMaster();
      if (!cancelled) {
        setIsMaster(result);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { isMaster, loading };
};
